import { InsightsInterface } from "@/interface/customer/customers.interface";
import {
  FavoriteOrderOptionType,
  FavoritePaymentType,
  InsightsFavoriteMoneyItemType,
  InsightsFavoriteQuantityItemType,
  InsightsType,
} from "@/types/customer/customer.type";
import { Translate as $t } from "@/plugins/translate";
import Storage from "@/services/storage";
import { StorageConstants } from "@/enums/storage.enum";
import { PriceMoney } from "@/modules/location";

export class CustomerInsights implements InsightsInterface {
  newPriceMoney = new PriceMoney({ amount: 0 });
  id: string = "";
  name: string = "";
  phone_number: string = "";
  last_dine_in?: string;
  last_delivery?: string;
  last_transaction: string = "";
  last_transaction_value: PriceMoney = this.newPriceMoney;
  transactions: number = 0; //total number of transactions
  items: number = 0; //total items count
  net_sales: PriceMoney = this.newPriceMoney; //total money spent
  discount: PriceMoney = this.newPriceMoney; //discount total
  number_of_visits: number = 0; // number of visits
  average_basket_size: number = 0; //average basket size
  average_sales: PriceMoney = this.newPriceMoney; //average bill
  favorite_items_quantity: Array<InsightsFavoriteQuantityItemType> = [];
  favorite_items_money_spent: Array<InsightsFavoriteMoneyItemType> = [];
  favorite_payment_method?: FavoritePaymentType;
  favorite_order_option?: FavoriteOrderOptionType;
  currency: string = "";

  constructor(data?: InsightsType) {
    if (!data) {
      return Object.assign(this, data);
    }
    this.id = data.id;
    this.name = data.name;
    this.phone_number = data.phone_number;
    this.last_dine_in = data.last_dine_in || "";
    this.last_delivery = data.last_delivery || "";
    this.last_transaction = data.last_transaction;
    this.last_transaction_value = new PriceMoney({ amount: data.last_transaction_value });
    this.transactions = data.transactions;
    this.items = data.items;
    this.net_sales = new PriceMoney({ amount: data.net_sales });
    this.average_sales = new PriceMoney({ amount: data.average_sales });
    this.discount = new PriceMoney({ amount: data.discount });
    this.id = data.id;
    this.number_of_visits = data.number_of_visits;
    this.average_basket_size = data.average_basket_size;
    this.favorite_items_quantity = data.favorite_items_quantity;
    this.favorite_items_money_spent = data.favorite_items_money_spent;
    this.favorite_payment_method = data.favorite_payment_method;
    this.favorite_order_option = data.favorite_order_option;
    this.currency = Storage.get(StorageConstants.BUSINESS)?.currency;
  }

  public formatted(): any {
    const WALLET = this.getWallet();
    const TRANSACTIONS = this.getTransactions();
    const FAVORITES = this.getFavorites();
    const LOYALTY_MEASURE = this.getLoyalty();
    const HISTORY = this.getHistory();

    return {
      WALLET,
      TRANSACTIONS,
      FAVORITES,
      LOYALTY_MEASURE,
      HISTORY,
    };
  }

  /***
   * Get favorites object
   * @return {any}
   */
  public getFavorites(): any {
    let index = 0;
    const favorite = this.favorite_items_quantity.map((item) => ({
      title: `${$t("favorite_product")} ${++index}`,
      icon: `favorites/star_${index}.svg`,
      value: item.name,
    }));

    favorite.push({
      title: $t("favorite_order_option"),
      icon: "favorites/thunb.svg",
      value: this.favorite_order_option?.name || "",
    });
    return favorite;
  }

  /***
   * Get wallet object
   * @return {any}
   */
  public getWallet(): any {
    return [
      {
        title: $t("account_receivable"),
        icon: "wallet/account.svg",
        value: this.net_sales?.getPricePackage(),
      },
    ];
  }

  /***
   * Get transactions object
   * @return {any}
   */
  public getTransactions(): any {
    return [
      {
        title: $t("average_bill"),
        icon: "transactions/bill.svg",
        value: this.average_sales?.getPricePackage(),
      },
      {
        title: $t("average_basket_size"),
        icon: "transactions/basket.svg",
        value: this.average_basket_size.toFixed(1),
      },
      {
        title: $t("most_used_payment"),
        icon: "transactions/payment.svg",
        value: this.favorite_payment_method?.name,
      },
      {
        title: $t("total_money_spent"),
        icon: "transactions/total.svg",
        value: this.net_sales?.getPricePackage(),
      },
      {
        title: $t("discount_total"),
        icon: "transactions/discount.svg",
        value: this.discount?.getPricePackage(),
      },
      {
        title: $t("total_items_count"),
        icon: "transactions/total-items.svg",
        value: this.items,
      },
    ];
  }

  /***
   * Get loyalty object
   * @return {any}
   */
  public getLoyalty(): any {
    return [
      {
        title: $t("number_of_visits"),
        icon: "loyalty/number_visits.svg",
        value: this.number_of_visits,
      },
    ];
  }

  /***
   * Get history object
   * @return {any}
   */
  public getHistory(): any {
    return [
      {
        title: $t("last_transaction"),
        icon: "history/transaction.svg",
        value: this.last_transaction ? window.moment(this.last_transaction).format("YYYY-MM-DD") : "-",
      },
      {
        title: $t("last_dine_in"),
        icon: "history/transaction.svg",
        value: this.last_dine_in ? window.moment(this.last_dine_in).format("YYYY-MM-DD") : "-",
      },
      {
        title: $t("last_delivery"),
        icon: "history/delivery.svg",
        value: this.last_delivery ? window.moment(this.last_delivery).format("YYYY-MM-DD") : "-",
      },
    ];
  }
}
